import React, { ReactNode, useCallback, useMemo, useState } from 'react';

export type AccordionProps = {
  presetValues?: boolean[];
  onlyOneActive?: boolean;
  children: ReactNode | ReactNode[];
  dataTest?: string;
  onItemToggle?: (index: number) => void;
};

// NOTE: Component children will need use these props
export type EnrichedAccordionChildProps = {
  expand?: boolean;
  toggleItemState?: () => void;
};

export default function Accordion({
  presetValues,
  children,
  onlyOneActive = false,
  dataTest,
  onItemToggle,
}: AccordionProps) {
  const defaultStateList = useMemo(
    () => new Array(React.Children.count(children)).fill(false),
    [children],
  );

  // In single-active mode preset values are ignored to avoid multiple items starting expanded
  const [stateList, setStateList] = useState<boolean[]>(
    onlyOneActive
      ? defaultStateList
      : defaultStateList.map((val, index) => presetValues?.[index] || val),
  );

  const toggleItemState = useCallback(
    (index: number) => () => {
      onItemToggle && onItemToggle(index);
      setStateList((prev) => {
        // In single-active mode collapse all items first, then toggle the clicked one
        const newStateList = onlyOneActive
          ? new Array(prev.length).fill(false)
          : Array.from(prev);

        // If single-active and the item was already open, clicking again closes it
        newStateList[index] =
          onlyOneActive && prev[index] ? false : !newStateList[index];

        return newStateList;
      });
    },
    [onItemToggle, onlyOneActive],
  );

  return (
    <div data-test={dataTest}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement<EnrichedAccordionChildProps>(child)) {
          return child;
        }

        return React.cloneElement(child, {
          ...child.props,
          expand: stateList[index],
          toggleItemState: toggleItemState(index),
        });
      })}
    </div>
  );
}
